<!-- WelcomeFreePlanModal.vue
     Modal de boas-vindas exibido após criação e verificação da conta.

     UX definida:
     - Usuário entra no plano Free sem fricção.
     - "Começar agora" é o CTA principal.
     - "Ver planos" é secundário promocional, amarelo suave, com foguete.
     - "Ver planos" abre o catálogo internamente, sem navegar de rota.
     - Ao fechar o catálogo, o WelcomeFreePlanModal permanece exibido.
-->

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import UpgradePage from "@/components/User/UpgradePage.vue";

const emit = defineEmits(["start", "plans"]);

// ── PATCH: controla o catálogo de planos dentro do próprio modal ──────────
const showPlanCatalog = ref(false);
// ── FIM PATCH ─────────────────────────────────────────────────────────────

function start() {
  emit("start");
}

// ── PATCH: antes emitia "plans" para o pai navegar para UpgradePage.
// Agora abre o catálogo localmente, preservando o WelcomeFreePlanModal.
function viewPlans() {
  showPlanCatalog.value = true;
}
// ── FIM PATCH ─────────────────────────────────────────────────────────────

function closePlanCatalogOnly() {
  showPlanCatalog.value = false;
}

function finishPlansFlow() {
  showPlanCatalog.value = false;
  start();
}

function handleEsc(e) {
  if (e.key === "Escape") {
    if (showPlanCatalog.value) {
      closePlanCatalogOnly();
      return;
    }

    start();
  }
}

onMounted(() => {
  document.addEventListener("keydown", handleEsc);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEsc);
});
</script>

<template>
  <transition name="modal-fade" appear>
    <div
      class="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-free-title"      
    >
      <div class="modal-panel modal-panel--compact">
        <div class="modal-panel__logo-wrap">
          <img
            src="/litterae_pena_fundo_transparente.png"
            alt="Logo Litterae"
            class="modal-panel__logo"
          />
        </div>

        <h4 id="welcome-free-title" class="modal-panel__title">
          Bem-vindo ao Litterae
        </h4>

        <p class="modal-panel__body">
          Sua conta foi criada com sucesso. Você já pode começar a usar o
          Litterae no plano <strong>Free</strong>, com acesso a todas 
          funcionalidades.
        </p>

        <p class="modal-panel__upgrade-banner">
          <i class="fas fa-circle-info" aria-hidden="true"></i>
          <span>
            Quando precisar de mais espaço ou recursos, você poderá atualizar
            seu plano a qualquer momento.
          </span>
        </p>

        <div class="modal-panel__actions">
          <button type="button" class="btn btn-plans" @click="viewPlans">
            <i class="fas fa-rocket" aria-hidden="true"></i>
            <span>Ver planos</span>
          </button>

          <button type="button" class="btn btn-primary" @click="start">
            Começar agora
          </button>
        </div>
      </div>

      <!-- ── PATCH: catálogo aberto como modal interno, sem navegação de rota ── -->
      <UpgradePage
        v-if="showPlanCatalog"
        :initial-step="2"
        :redirect-on-catalog-close="false"
        @close-upgrade-modal="closePlanCatalogOnly"
        @close="finishPlansFlow"
        @managed-accounts-needed="finishPlansFlow"
      />
      <!-- ── FIM PATCH ──────────────────────────────────────────────────────── -->
    </div>
  </transition>
</template>

<style scoped>
/* Respiro e alinhamento vêm do .modal-panel--compact; aqui só a posição. */
.modal-panel {
  margin-top: 4rem;
}

.modal-panel__logo-wrap {
  width: 58px;
  height: 58px;
  border-radius: 16px;
  background: var(--surface-2);
  border: 1px solid var(--neutral-200);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.15rem;
}

.modal-panel__logo {
  width: 42px;
  height: 42px;
  object-fit: contain;
}

.modal-panel__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-dark);
  margin: 0;
}

.modal-panel__body {
  font-size: 0.9rem;
  color: var(--text-light);
  line-height: 1.5;
  margin: 0;
  text-align: justify;
}

.modal-panel__upgrade-banner {
  width: 100%;
  font-size: 0.82rem;
  background: var(--warning-bg);
  border: 1px solid var(--warning-bg);
  color: var(--warning-text);
  border-radius: 10px;
  padding: 0.55rem 0.7rem;
  line-height: 1.45;
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  margin: 0;
  text-align: left;
}

.modal-panel__upgrade-banner i {
  margin-top: 0.1rem;
  color: var(--warning);
  flex-shrink: 0;
}

.modal-panel__actions {
  display: flex;
  gap: 0.65rem;
  margin-top: 0.5rem;
}

.btn-plans {
  background-color: var(--warning-bg);
  color: var(--warning-text);
  border-color: var(--warning-bg);
}

.btn-plans:hover {
  background-color: var(--warning-bg);
  border-color: #fcd34d;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-active .modal-panel,
.modal-fade-leave-active .modal-panel {
  transition: transform 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .modal-panel,
.modal-fade-leave-to .modal-panel {
  transform: scale(0.96) translateY(-8px);
}

@media (max-width: 480px) {
  .modal-backdrop {
    padding: 6rem 1rem 1rem;
  }

  .modal-panel__actions {
    width: 100%;
    flex-direction: column-reverse;
  }

  .btn {
    width: 100%;
  }
}
</style>